import { createFileRoute, Link } from "@tanstack/react-router"
import { Target } from "lucide-react"
import { useMemo } from "react"
import z from "zod"
import { Page } from "@/components/page"
import { Button } from "@/components/ui/button"
import { EstateTotals } from "@/components/ui/estate-totals"
import { Typography } from "@/components/ui/typography"
import { STEPS } from "@/constants/steps"
import { trpc } from "@/lib/trpc"
import { GuidedForm } from "../../../../components/forms/guided-form"

type StepId = (typeof STEPS)[number]["id"]

export const searchSchema = z.object({
  currentStep: z
    .enum([...STEPS.map((s) => s.id)] as [StepId, ...StepId[]])
    .optional()
    .catch("intro"),
  tab: z.string().optional(),
  journalId: z.string().optional(),
})

export const Route = createFileRoute("/_authed/_individual/personal/guided-forms")({
  component: RouteComponent,
  validateSearch: (search) => searchSchema.parse(search),
})
function RouteComponent() {
  const search = Route.useSearch()

  // Get all estates the individual has access to
  const {
    data: estates = [],
    isLoading: estatesLoading,
    error: estatesError,
  } = trpc.estates.list.useQuery()

  // Pick a "primary" estate:
  //    - Prefer an Admin, non-journal-only estate
  //    - Fallback to the first estate if nothing matches
  const primaryEstate = useMemo(() => {
    if (!estates.length) return undefined
    const adminNonJournal = estates.find((e) => e.accessLevel === "Admin" && !e.journalOnly)
    return adminNonJournal ?? estates[0]
  }, [estates])

  return (
    <Page
      headerSlot={
        <div className="flex w-full items-center justify-between gap-4">
          <Typography as="h1" size="2xl">
            Guided Forms
          </Typography>

          <Button className="hidden md:flex" variant="outline" asChild>
            <Link to="/personal/goals">
              <Target className="mr-1 size-4" aria-hidden="true" />
              Go to Goals
            </Link>
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {!estatesLoading && !estatesError && primaryEstate && (
          <section className="flex w-full flex-col gap-4 rounded-xl border bg-muted/30 p-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <Typography as="h2" size="lg" className="font-semibold">
                Complete your estate plan
              </Typography>

              <Typography as="p" className="mt-1 text-sm text-muted-foreground">
                Work through each section to build a complete record of your estate.
              </Typography>
            </div>

            <div className="w-full lg:w-auto">
              <EstateTotals
                estateId={primaryEstate.id}
                className="!mb-0 !mt-0 !grid !w-full !grid-cols-2 gap-4 !space-x-0 !space-y-0"
              />
            </div>
          </section>
        )}

        <GuidedForm currentStep={search.currentStep ?? "intro"} />
      </div>
    </Page>
  )
}
